import React, { useState, useRef, useEffect } from 'react';
import { Overlay } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

/**
 * ExpandableNote Component
 * Displays truncated note text with a "Show More" option for large notes.
 * Supports both:
 * 1. Expand-on-Hover: Floating popover preview with the complete note content.
 * 2. Expand-on-Click: In-place inline expansion with a "Show Less" toggle.
 *
 * @param {string} text - Note content
 * @param {number} maxLength - Character threshold for truncation (default: 60)
 * @param {string} title - Header title for the hover popover (default: "Revision Notes")
 * @param {string} placement - Popover placement (default: "auto")
 */
const ExpandableNote = ({
  text,
  maxLength = 60,
  title = "Revision Notes",
  placement = "auto"
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const targetRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Handle empty or null values
  if (!text || text === 'null' || (typeof text === 'string' && text.trim() === '')) {
    return <span className="text-muted">N/A</span>;
  }

  const rawText = String(text);
  const hasNewlines = rawText.includes('\n');
  const isLarge = rawText.length > maxLength || hasNewlines;

  // Short notes render directly without controls
  if (!isLarge) {
    return <span style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{rawText}</span>;
  }

  // Calculate truncated text snippet
  let truncatedText = '';
  if (hasNewlines) {
    const firstLine = rawText.split('\n')[0];
    truncatedText = firstLine.length > maxLength
      ? `${firstLine.slice(0, maxLength).trim()}...`
      : `${firstLine.trim()}...`;
  } else {
    truncatedText = `${rawText.slice(0, maxLength).trim()}...`;
  }

  const handleMouseEnter = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setShowPopover(true);
  };

  const handleMouseLeave = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setShowPopover(false);
    }, 250);
  };

  const handleToggleExpand = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setShowPopover(false);
    setIsExpanded((prev) => !prev);
  };

  // In-place expanded view
  if (isExpanded) {
    return (
      <div className="expandable-note-expanded" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
        <span>{rawText}</span>{' '}
        <button
          type="button"
          className="btn btn-link p-0 text-primary fw-semibold expandable-note-btn text-decoration-underline"
          style={{ fontSize: '0.85rem', verticalAlign: 'baseline' }}
          onClick={handleToggleExpand}
          aria-expanded="true"
        >
          Show Less <FontAwesomeIcon icon={faChevronUp} style={{ fontSize: '10px', marginLeft: '2px' }} />
        </button>
      </div>
    );
  }

  // Collapsed view with hover popover and click expand
  return (
    <div className="expandable-note-wrapper" style={{ display: 'inline' }}>
      <span
        ref={targetRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="expandable-note-trigger"
      >
        <span style={{ wordBreak: 'break-word' }}>{truncatedText}</span>{' '}
        <button
          type="button"
          className="btn btn-link p-0 text-primary fw-semibold expandable-note-btn text-decoration-underline"
          style={{ fontSize: '0.85rem', verticalAlign: 'baseline' }}
          onClick={handleToggleExpand}
          aria-expanded="false"
        >
          Show More <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '10px', marginLeft: '2px' }} />
        </button>
      </span>

      <Overlay
        target={targetRef.current}
        show={showPopover && !isExpanded}
        placement={placement}
        rootClose
        onHide={() => setShowPopover(false)}
      >
        {({ placement: _p, arrowProps: _ap, show: _s, popper: _pop, hasDoneInitialMeasure: _h, ...overlayProps }) => (
          <div
            {...overlayProps}
            ref={overlayProps.ref}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className="popover shadow-lg"
            style={{
              ...overlayProps.style,
              position: 'absolute',
              zIndex: 1060,
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
              maxWidth: '380px',
              minWidth: '240px',
              width: 'max-content',
            }}
          >
            <div
              className="popover-header d-flex justify-content-between align-items-center py-2 px-3"
              style={{
                backgroundColor: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
                borderTopLeftRadius: '7px',
                borderTopRightRadius: '7px',
              }}
            >
              <div className="d-flex align-items-center">
                <i className="uil uil-file-alt text-primary me-2 font-size-16"></i>
                <span className="fw-semibold text-dark" style={{ fontSize: '13px' }}>
                  {title}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowPopover(false)}
                className="btn-close"
                style={{ fontSize: '10px', padding: '4px', cursor: 'pointer' }}
                aria-label="Close"
              />
            </div>
            <div
              className="popover-body p-3"
              style={{
                maxHeight: '260px',
                overflowY: 'auto',
                fontSize: '13px',
                lineHeight: '1.5',
                color: '#1e293b',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {rawText}
            </div>
            <div
              className="px-3 py-2 bg-light border-top text-end"
              style={{
                borderBottomLeftRadius: '7px',
                borderBottomRightRadius: '7px',
              }}
            >
              <button
                type="button"
                className="btn btn-sm btn-link p-0 text-primary fw-semibold"
                style={{ fontSize: '12px', textDecoration: 'none' }}
                onClick={handleToggleExpand}
              >
                Expand Inline <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '10px', marginLeft: '2px' }} />
              </button>
            </div>
          </div>
        )}
      </Overlay>
    </div>
  );
};

export default ExpandableNote;
